import { useSnackbar } from 'notistack'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetWaitingRoomQuery } from '@/core/services/waiting-room'

import { useNewPatients } from './useNewPatients'

export type NotificationPermissionState = NotificationPermission | 'unsupported'

function currentPermission(): NotificationPermissionState {
  return 'Notification' in window ? Notification.permission : 'unsupported'
}

export function useToggleNotifications(waitingRoomId: string) {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const { data: waitingRoom } = useGetWaitingRoomQuery(waitingRoomId, { refetchOnMountOrArgChange: true })
  const waitingRoomName = waitingRoom?.name || 'Waiting Room'

  // Compare against the literal 'true' — Boolean('false') is true, which would re-enable a stored "false".
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    () => localStorage.getItem('waitingRoomNotificationsEnabled') === 'true'
  )
  const [permission, setPermission] = useState<NotificationPermissionState>(currentPermission)
  // Whether the doctor has confirmed (via the test) that banners actually reach them — the only way to
  // know, since the web platform exposes no signal for macOS OS-level / Focus suppression.
  const [verified, setVerified] = useState(() => localStorage.getItem('waitingRoomNotificationsVerified') === 'true')

  function showNotification(title: string, body: string, tag: string): boolean {
    try {
      const options: NotificationOptions & { renotify?: boolean } = {
        body,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag,
        // Re-alert on each arrival instead of silently replacing the previous (still-open) banner.
        renotify: true,
        requireInteraction: false,
      }
      const notification = new Notification(title, options)
      setTimeout(() => notification.close(), 6000)
      notification.onclick = () => {
        window.focus()
        notification.close()
      }
      return true
    } catch (_error) {
      return false
    }
  }

  // Show a browser notification when patients actually arrive (only when enabled and permitted).
  useNewPatients(waitingRoomId, (newPatientsCount) => {
    if (!notificationsEnabled || !('Notification' in window) || Notification.permission !== 'granted') return
    showNotification(
      t('page.waitingRoom.notification.newPatient.title'),
      t('page.waitingRoom.notification.newPatient.body', { count: newPatientsCount, roomName: waitingRoomName }),
      'waiting-room-new-patient'
    )
  })

  // Request permission if needed; returns the resulting permission state.
  const ensurePermission = useCallback(async (): Promise<NotificationPermissionState> => {
    if (!('Notification' in window)) {
      setPermission('unsupported')
      return 'unsupported'
    }
    if (Notification.permission === 'default') {
      try {
        const result = await Notification.requestPermission()
        setPermission(result)
        return result
      } catch (_error) {
        setPermission(Notification.permission)
        return Notification.permission
      }
    }
    setPermission(Notification.permission)
    return Notification.permission
  }, [])

  const toggleNotifications = async () => {
    const newNotificationState = !notificationsEnabled
    if (newNotificationState && 'Notification' in window) {
      const result = await ensurePermission()
      if (result !== 'granted') {
        enqueueSnackbar(t('page.waitingRoom.notification.permissionDenied'), { variant: 'warning' })
        return
      }
    }
    setNotificationsEnabled(newNotificationState)
    localStorage.setItem('waitingRoomNotificationsEnabled', `${newNotificationState}`)
  }

  // Fire a real notification right now so the doctor can confirm it reaches them (test-and-verify is the
  // only reliable check on macOS, where a "granted" permission can still be silently OS-suppressed).
  const sendTestNotification = useCallback(async (): Promise<boolean> => {
    const result = await ensurePermission()
    if (result !== 'granted') {
      enqueueSnackbar(t('page.waitingRoom.notification.permissionDenied'), { variant: 'warning' })
      return false
    }
    return showNotification(
      t('page.waitingRoom.notification.test.notificationTitle'),
      t('page.waitingRoom.notification.test.notificationBody'),
      'waiting-room-test'
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueueSnackbar, t, ensurePermission, waitingRoomName])

  const confirmVerified = useCallback(
    (didSee: boolean) => {
      setVerified(didSee)
      localStorage.setItem('waitingRoomNotificationsVerified', `${didSee}`)
      if (didSee && !notificationsEnabled) {
        setNotificationsEnabled(true)
        localStorage.setItem('waitingRoomNotificationsEnabled', 'true')
      }
    },
    [notificationsEnabled]
  )

  return {
    notificationsEnabled,
    toggleNotifications,
    permission,
    verified,
    sendTestNotification,
    confirmVerified,
  }
}
